import axios from 'axios';
import { ActionTree } from 'vuex';
import { RootState } from '@/store/store';
import { UploadVideoState, state as defaultState } from '@/video-old/store/upload-video.store';
import {
  mutationVideoIsUploading,
  mutationUploadingStatus,
  mutationStartRecord,
  mutationSetDocumentId,
  mutationVideoIsSaving,
  mutationSetVideoId,
  mutationSetVideoStreamStatus,
  mutationSetState,
} from '@/video-old/store/upload-video.mutations';
import { VideoService } from '@/api/video/services/video.service';
import { EVideoSource } from '@/video/types/enums/video-source';
import { IVideo } from '@/types/Video';
import { IEngagementVideo } from '@lib-shared/types/engagement-video';

export const actionStartRecord = 'startRecord';
export const actionSetVideo = 'setVideo';
export const actionResetUploadVideo = 'cancelVideoUpload';
export const actionSetVideoStreamStatus = 'setVideoStreamStatus';
export const actionUploadVideo = 'uploadVideo';
export const actionUploadVideoSuccess = 'uploadVideoSuccess';
export const actionUploadVideoFailed = 'uploadVideoFailed';
export const actionSendAppInvite = 'sendAppInvite';

export enum VideoUploadingStatus {
  saving = 'saving',
  saved = 'saved',
  complete = 'complete',
  failed = 'failed',
  success = 'success',
}

export const actions: ActionTree<UploadVideoState, RootState> = {
  [actionStartRecord]({ commit }, documentId) {
    commit(mutationStartRecord, true);
    commit(mutationSetDocumentId, documentId);
  },
  async [actionSetVideo]({ commit, dispatch }, { documentId, videoId }) {
    commit(mutationVideoIsSaving, VideoUploadingStatus.saving);
    const response = await VideoService.addEngageVideo(videoId, documentId);
    if (response) {
      const video: IVideo = await VideoService.loadEngageVideo(documentId);
      const engageVideo: IEngagementVideo = {
        id: video.id,
        video,
      };
      dispatch('documentEdit/assembler/addEngageVideo', engageVideo, {
        root: true,
      });

      commit(mutationSetVideoId, videoId);
      commit(mutationUploadingStatus, VideoUploadingStatus.saved);
    } else {
      commit(mutationUploadingStatus, VideoUploadingStatus.failed);
    }
  },
  [actionResetUploadVideo]({ commit }) {
    commit(mutationSetState, { ...defaultState });
  },
  [actionSetVideoStreamStatus]({ commit }, status) {
    commit(mutationSetVideoStreamStatus, status);
  },
  async [actionUploadVideo](
    { commit, dispatch },
    {
      file,
      filename,
      documentId,
      videoThumb,
      isEngageVideo,
      published,
    }: {
      file: File;
      filename: string;
      documentId: string;
      videoThumb: string;
      isEngageVideo?: boolean;
      published?: boolean;
    }
  ) {
    commit(mutationVideoIsSaving, VideoUploadingStatus.saving);
    commit(mutationStartRecord, false);
    try {
      const {
        video,
        uploadLink,
      }: { video: IVideo; uploadLink: string } = await VideoService.createVideo({
        videoType: EVideoSource.Upload,
        videoTitle: filename,
        published,
      });
      if (isEngageVideo) {
        await VideoService.addEngageVideo(video.id, documentId);
      }
      await VideoService.uploadVideo({ file, uploadLink });
      await VideoService.uploadVideoDone(video.id);
      const updatedVideo: IVideo = { ...video, thumbUrl: videoThumb };
      dispatch(actionUploadVideoSuccess, { video: updatedVideo, isEngageVideo });
    } catch (e) {
      dispatch(actionUploadVideoFailed);
    }
  },
  [actionUploadVideoFailed]({ commit }) {
    commit(mutationVideoIsUploading, false);
    commit(mutationUploadingStatus, VideoUploadingStatus.failed);
  },
  [actionUploadVideoSuccess](
    { commit, dispatch },
    { video, isEngageVideo }: { video: IVideo; isEngageVideo: boolean }
  ) {
    if (isEngageVideo) {
      const engageVideo: IEngagementVideo = {
        id: video.id,
        video,
      };
      dispatch('documentEdit/assembler/addEngageVideo', engageVideo, {
        root: true,
      });
    }
    commit(mutationSetVideoId, video.id);
    commit(mutationUploadingStatus, VideoUploadingStatus.complete);
    commit(mutationVideoIsUploading, false);
  },
  async [actionSendAppInvite](
    _,
    {
      mobileNumber,
      userId,
    }: {
      mobileNumber: string;
      userId: string;
    }
  ) {
    const body = new FormData();
    body.append('action', 'sendapp');
    body.append('mobile', mobileNumber);
    body.append('user-id', userId);

    const {
      data: { status },
    } = await axios.post(`/save/user`, body);
    return status === 1;
  },
};
